import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from '../components/common/Button';
import {ReportData, fetchReportList, createReport} from '../api';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// --- [Styles] Common ---
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-main);
  color: var(--text-bright);
`;

const DateGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--bg-input);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
`;

const DateInput = styled.input`
  background-color: transparent;
  color: var(--text-bright);
  border: none;
  padding: 0.2rem;
  cursor: pointer;
  font-family: inherit;
  &::-webkit-calendar-picker-indicator { filter: invert(1); cursor: pointer; }
  &:focus { outline: none; }
`;

const DateSeparator = styled.span`color: var(--text-dark);`;

// --- [Styles] List View ---
const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background-color: var(--bg-list); border-radius: 4px; }
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
`;

const ReportCard = styled.div`
  background-color: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  box-shadow: 0 2px 4px var(--shadow-light);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    border-color: var(--primary-accent);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const ReportPeriodBadge = styled.div`
  background-color: var(--primary-accent);
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
`;

const CardDate = styled.span`
  font-size: 0.8rem;
  color: var(--text-dark);
`;

const CardPreview = styled.div`
  font-size: 0.9rem;
  color: var(--text-normal);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* 3줄까지만 표시 */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: var(--bg-input);
  padding: 0.8rem;
  border-radius: 4px;
`;

// ✨ [신규] 페이지네이션 스타일
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
  margin-bottom: 2rem;
`;

const PageInfo = styled.span`
  color: var(--text-normal);
  font-size: 0.9rem;
`;

const PageButton = styled.button`
  background-color: var(--bg-card);
  border: 1px solid var(--border-color);
  color: var(--text-normal);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: var(--bg-input);
    color: var(--text-bright);
    border-color: var(--primary-accent);
  }
`;

// --- [Styles] Modal & Markdown ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  padding: 2rem;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 900px;
  height: 90vh;
  background-color: #ffffff; /* 종이 질감 */
  color: #24292e;
  border-radius: 4px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 3rem; /* A4 용지 여백 느낌 */

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background-color: #ccc; border-radius: 4px; }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  &:hover { color: #333; }
`;

const MarkdownView = styled.div`
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #333;

  h1, h2, h3 { margin-top: 1.5em; margin-bottom: 0.8em; font-weight: 700; color: #111; }
  h1 { font-size: 1.8rem; border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
  h2 { font-size: 1.5rem; }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    font-size: 0.95rem;
  }
  th, td {
    border: 1px solid #dfe2e5;
    padding: 0.75rem;
    text-align: left;
  }
  th { background-color: #f6f8fa; font-weight: 700; color: #24292e; }
  tr:nth-child(2n) { background-color: #fcfcfc; }
  
  blockquote {
    margin: 1rem 0;
    padding: 0.5rem 1rem;
    border-left: 4px solid #0052cc;
    background-color: #f0f4ff;
    color: #444;
  }
  
  ul, ol { padding-left: 1.5em; }
`;

const EmptyState = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: var(--text-dark);
  gap: 1rem;
  svg { width: 64px; height: 64px; opacity: 0.5; }
`;

// --- [Components] ---

// 상세 모달 컴포넌트
const ReportDetailModal: React.FC<{ report: ReportData, onClose: () => void }> = ({ report, onClose }) => {
    // 배경 클릭 시 닫기
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(report.report);
        alert('마크다운 내용이 복사되었습니다.');
    };

    return (
        <ModalOverlay onClick={handleBackdropClick}>
            <ModalContent>
                <ModalHeader>
                    <div>
                        <ModalTitle>업무 보고서 상세</ModalTitle>
                        <span style={{fontSize: '0.85rem', color: '#666'}}>
                            기간: {report.startDate} ~ {report.endDate}
                        </span>
                    </div>
                    <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
                        <Button onClick={handleCopy} style={{fontSize: '0.8rem', padding: '0.3rem 0.8rem'}}>복사하기</Button>
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <MarkdownView>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {report.report}
                        </ReactMarkdown>
                    </MarkdownView>
                </ModalBody>
            </ModalContent>
        </ModalOverlay>
    );
};

const ActivityReportPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // 날짜 상태 (생성용)
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    // 리스트 및 페이지네이션 상태
    const [reportList, setReportList] = useState<ReportData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [currentPage, setCurrentPage] = useState(0); // ✨ 현재 페이지 (0부터 시작)
    const [totalPages, setTotalPages] = useState(0);   // ✨ 전체 페이지 수
    const pageSize = 12; // 12개가 3열/4열 그리드에 적당함

    // 모달 상태 (URL 파라미터 기반)
    const selectedReportId = searchParams.get('reportId');
    const selectedReport = reportList.find(r => r.id === selectedReportId);

    // 1. 리포트 목록 조회
    const loadReports = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchReportList(currentPage, pageSize);
            setReportList(data.content);
            setTotalPages(data.totalPages);
        } catch (error) {
            console.error("Failed to fetch reports", error);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage]); // ✨ currentPage 변경 시 재실행

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    // 2. 리포트 생성 핸들러
    const handleGenerateReport = async () => {
        if (!startDate || !endDate) return alert("날짜를 선택해주세요.");

        setIsCreating(true);
        try {
            const newReport = await createReport(startDate, endDate);

            // ✨ 생성 완료 시 1페이지로 이동하고 목록 갱신
            if (currentPage !== 0) {
                setCurrentPage(0); // 이 경우 useEffect에 의해 loadReports 자동 호출됨
            } else {
                await loadReports(); // 이미 0페이지라면 강제 갱신
            }

            // 생성 완료 후 해당 리포트 상세 모달 열기
            setSearchParams({ reportId: newReport.id });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "리포트 생성에 실패했습니다.";
            alert(`🚫 리포트 생성 실패\n\n내용: ${errorMessage}`);
        } finally {
            setIsCreating(false);
        }
    };

    // 3. 페이지네이션 핸들러
    const handlePrevPage = () => {
        if (currentPage > 0) setCurrentPage(prev => prev - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) setCurrentPage(prev => prev + 1);
    };

    // 4. 리포트 클릭 핸들러 (라우팅)
    const handleReportClick = (id: string) => {
        setSearchParams({ reportId: id });
    };

    // 5. 모달 닫기 핸들러
    const handleCloseModal = () => {
        setSearchParams({}); // URL 파라미터 제거
    };

    const LeftContent = (
        <>
            <DateGroup>
                <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <DateSeparator>~</DateSeparator>
                <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </DateGroup>
            <Button onClick={handleGenerateReport} disabled={isCreating}>
                {isCreating ? '✨ 생성 중...' : '✨ 새 리포트 생성'}
            </Button>
        </>
    );

    return (
        <PageWrapper>
            <ResponsiveHeader
                title="📑 업무 보고서 목록"
                leftContent={LeftContent}
            />
            <ListContainer>
                {isLoading && reportList.length === 0 ? (
                    <EmptyState>
                        <span>로딩 중...</span>
                    </EmptyState>
                ) : reportList.length === 0 ? (
                    <EmptyState>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                        <span>생성된 리포트가 없습니다.</span>
                    </EmptyState>
                ) : (
                    <>
                        <GridWrapper>
                            {reportList.map(report => (
                                <ReportCard key={report.id} onClick={() => handleReportClick(report.id)}>
                                    <CardHeader>
                                        <ReportPeriodBadge>
                                            {report.startDate} ~ {report.endDate}
                                        </ReportPeriodBadge>
                                        <CardDate>
                                            작성일: {report.createDateTime.split('T')[0]}
                                        </CardDate>
                                    </CardHeader>
                                    {/* 마크다운 원본 중 앞부분만 텍스트로 보여주기 위해 정규식으로 태그 등 제거 (간이) */}
                                    <CardPreview>
                                        {report.report
                                            .replace(/[#*`]/g, '') // 마크다운 기호 대충 제거
                                            .substring(0, 150)}
                                        ...
                                    </CardPreview>
                                </ReportCard>
                            ))}
                        </GridWrapper>

                        {/* ✨ 페이지네이션 컨트롤 */}
                        <PaginationContainer>
                            <PageButton onClick={handlePrevPage} disabled={currentPage === 0}>
                                &lt; 이전
                            </PageButton>
                            <PageInfo>
                                {currentPage + 1} / {totalPages === 0 ? 1 : totalPages}
                            </PageInfo>
                            <PageButton onClick={handleNextPage} disabled={currentPage >= totalPages - 1}>
                                다음 &gt;
                            </PageButton>
                        </PaginationContainer>
                    </>
                )}
            </ListContainer>

            {/* URL에 reportId가 있고, 해당 리포트 데이터가 있으면 모달 표시 */}
            {selectedReportId && selectedReport && (
                <ReportDetailModal
                    report={selectedReport}
                    onClose={handleCloseModal}
                />
            )}
        </PageWrapper>
    );
};

export default ActivityReportPage;